import { useEffect, useState } from "react";
import { api, errorMessage, type AccountPatch } from "@/lib/api";
import { fractionToPct, pctToFraction } from "@/lib/units";
import { usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import type { Account, Decimal, ProxyPublic } from "@/lib/types";
import {
  Button,
  COMPUTER_ID_RE,
  ErrorNote,
  Field,
  SidePanel,
  inputClass,
  randomComputerId,
} from "@/components/ui";

function proxyName(p: Pick<ProxyPublic, "label" | "host" | "port">): string {
  return p.label ? `${p.label} · ${p.host}:${p.port}` : `${p.host}:${p.port}`;
}

/** Stringa vuota -> null: i campi facoltativi si svuotano, non si salvano come "". */
const orNull = (v: string) => (v.trim() === "" ? null : v.trim());

/**
 * Password del cliente: si GENERA, non si digita.
 *
 * `crypto.getRandomValues` e non `Math.random`, che è prevedibile. Niente
 * caratteri ambigui (0/O, 1/l/I): questa password viene letta ad alta voce o
 * copiata a mano, e una «l» scambiata per «1» diventa una telefonata.
 */
const PASSWORD_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
function generatePassword(length = 20): string {
  const bytes = new Uint32Array(length);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]).join("");
}

// ---------------------------------------------------------------------------
// CID con pulsante genera
// ---------------------------------------------------------------------------

function ComputerIdInput({
  id,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const invalid = value !== "" && !COMPUTER_ID_RE.test(value);
  return (
    <>
      <div className="flex gap-2">
        <input
          id={id}
          value={value}
          disabled={disabled}
          maxLength={32}
          spellCheck={false}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(/\s/g, "").toUpperCase())}
          className={`${inputClass} font-mono text-xs ${invalid ? "border-loss" : ""}`}
          aria-invalid={invalid}
        />
        <Button onClick={() => onChange(randomComputerId())} disabled={disabled}>
          Genera
        </Button>
      </div>
      {invalid && <p className="mt-1 text-xs text-loss">Servono 32 caratteri esadecimali ({value.length} di 32)</p>}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cliente
// ---------------------------------------------------------------------------

export interface ClientFormValue {
  id: string;
  name: string;
  email: string;
  notes: string | null;
  computerId: string;
  defaultProxyId: string | null;
  defaultProxy: ProxyPublic | null;
  /** Le due quote predefinite, come FRAZIONI. Il ciclo se le congela alla nascita. */
  defaultPayoutSplitPct: Decimal;
  defaultBurnSplitPct: Decimal;
}

/**
 * Il cliente si crea COMPLETO: anagrafica, il suo proxy e il suo CID.
 *
 * Un proxy per cliente (DECISIONE 19), quindi non c'è più un pool da cui
 * pescare e non c'è più una pagina Proxy: i campi del proxy stanno qui, e il
 * legame lo crea il backend insieme alla riga.
 *
 * I parametri del gateway — paese, città, sessione, durata — sono campi loro e
 * non suffissi dentro la password: su IPRoyal ci vivevano dentro, e per sapere
 * da che paese esce un cliente bisognava svelare il segreto.
 */
export function ClientPanel({
  open,
  client,
  onClose,
  onSaved,
}: {
  open: boolean;
  client: ClientFormValue | null;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const refresh = useRefreshDirectory();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [cid, setCid] = useState("");
  // Le percentuali si SCRIVONO come percentuali e viaggiano come frazioni: la
  // conversione sta in lib/units.ts e succede solo li'.
  const [payoutSplit, setPayoutSplit] = useState("70");
  const [burnSplit, setBurnSplit] = useState("80");

  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [type, setType] = useState<"SOCKS5" | "HTTP" | "HTTPS">("SOCKS5");
  const [provider, setProvider] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");

  // Accesso del cliente: solo alla CREAZIONE, e solo se lo si vuole.
  const [accessWanted, setAccessWanted] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");
  const [accessPassword, setAccessPassword] = useState("");
  /** La password appena creata, mostrata UNA volta: nel database c'è l'hash. */
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const proxy = client?.defaultProxy ?? null;

  useEffect(() => {
    if (!open) return;
    setName(client?.name ?? "");
    setEmail(client?.email ?? "");
    setAccessWanted(false);
    setAccessEmail("");
    setAccessPassword("");
    setCreated(null);
    setNotes(client?.notes ?? "");
    setCid(client?.computerId ?? "");
    setPayoutSplit(client ? fractionToPct(client.defaultPayoutSplitPct) : "70");
    setBurnSplit(client ? fractionToPct(client.defaultBurnSplitPct) : "80");
    setHost(proxy?.host ?? "");
    setPort(proxy ? String(proxy.port) : "");
    setType(proxy?.type ?? "SOCKS5");
    setProvider(proxy?.provider ?? "iproyal");
    setUsername(proxy?.username ?? "");
    setPassword("");
    setCountry(proxy?.country ?? "");
    setCity(proxy?.city ?? "");
    setError(null);
  }, [open, client, proxy]);

  const cidInvalid = cid !== "" && !COMPUTER_ID_RE.test(cid);
  const portNum = Number(port);
  const proxyWanted = host.trim() !== "" || port.trim() !== "";
  const proxyInvalid = proxyWanted && (host.trim() === "" || !Number.isInteger(portNum) || portNum < 1 || portNum > 65535);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body = {
      name: name.trim(),
      email: email.trim(),
      notes: orNull(notes),
      ...(pctToFraction(payoutSplit) ? { defaultPayoutSplitPct: pctToFraction(payoutSplit)! } : {}),
      ...(pctToFraction(burnSplit) ? { defaultBurnSplitPct: pctToFraction(burnSplit)! } : {}),
      ...(!client && accessWanted && accessPassword
        ? { access: { email: (accessEmail.trim() || email.trim()).toLowerCase(), password: accessPassword } }
        : {}),
    };
    try {
      const saved = client ? await api.clients.update(client.id, body) : await api.clients.create(body);

      const proxyBody = {
        provider: orNull(provider),
        host: host.trim(),
        port: portNum,
        type,
        username: orNull(username),
        country: orNull(country),
        city: orNull(city),
        ...(password ? { password } : {}),
      };
      if (proxyWanted && proxy) await api.proxies.update(proxy.id, proxyBody);
      else if (proxyWanted) await api.proxies.create({ ...proxyBody, clientId: saved.id });
      else if (proxy) {
        // Tolto dal form: si stacca e si cancella. Un proxy senza cliente non è
        // una riga libera nel pool, perché il pool non esiste più.
        await api.clients.update(saved.id, { defaultProxyId: null });
        await api.proxies.remove(proxy.id);
      }

      await refresh();
      // La password si mostra UNA volta, e il pannello resta aperto: chiuderlo
      // subito vorrebbe dire perderla, perché nel database c'è solo l'hash.
      if (!client && accessWanted && accessPassword) {
        setCreated({ email: (accessEmail.trim() || email.trim()).toLowerCase(), password: accessPassword });
        setAccessPassword("");
        return;
      }
      onSaved?.(saved.id);
      onClose();
    } catch (err) {
      await refresh(); // il cliente può essere stato creato prima dell'errore sul proxy
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={client ? "Modifica cliente" : "Nuovo cliente"}
      subtitle="Il proxy e il CID sono del cliente e valgono per i suoi conti. I conti si aggiungono dopo."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          {created ? (
            <Button variant="primary" onClick={onClose}>
              Ho copiato la password
            </Button>
          ) : (
            <Button variant="primary" type="submit" form="client-form" disabled={busy || cidInvalid || proxyInvalid}>
              {busy ? "Salvataggio…" : client ? "Salva" : "Crea cliente"}
            </Button>
          )}
        </>
      }
    >
      <form id="client-form" onSubmit={submit} className="space-y-4">
        <Field label="Nome">
          {(id) => <input id={id} required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />}
        </Field>
        <Field label="Email">
          {(id) => (
            <input id={id} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          )}
        </Field>
        {!client && (
          <fieldset className="space-y-3 rounded border border-line p-3">
            <legend className="px-1 text-sm text-muted">Accesso del cliente</legend>
            {created ? (
              <div role="status" className="space-y-2">
                <p className="text-sm text-ink">
                  Credenziali create. <strong>Si vedono solo adesso</strong>: nel database c'è soltanto l'hash, e non si
                  possono rileggere — si possono solo sostituire.
                </p>
                <dl className="space-y-1 rounded border border-line bg-surface px-3 py-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Email</dt>
                    <dd className="font-mono text-ink">{created.email}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Password</dt>
                    <dd className="select-all font-mono text-ink">{created.password}</dd>
                  </div>
                </dl>
                <p className="text-xs text-faint">
                  Al primo accesso il cliente dovrà cambiarla: una password generata da noi e comunicata su una chat non
                  resta un segreto. <strong>L'area cliente non è ancora attiva</strong>, quindi l'utente esiste ma non entra.
                </p>
              </div>
            ) : (
              <>
                <label className="flex items-center gap-2 text-sm text-muted">
                  <input type="checkbox" checked={accessWanted} onChange={(e) => setAccessWanted(e.target.checked)} />
                  Crea un utente per questo cliente
                </label>
                {accessWanted && (
                  <>
                    <Field label="Email d'accesso" hint="Vuota: quella del cliente. È unica per tenant, non nel mondo.">
                      {(id) => (
                        <input
                          id={id}
                          type="email"
                          value={accessEmail}
                          placeholder={email || "cliente@esempio.it"}
                          onChange={(e) => setAccessEmail(e.target.value)}
                          className={inputClass}
                        />
                      )}
                    </Field>
                    <Field label="Password" hint="Si genera, non si scrive: verrà mostrata una volta sola dopo il salvataggio.">
                      {(id) => (
                        <div className="flex gap-2">
                          <input id={id} readOnly value={accessPassword} placeholder="—" className={`${inputClass} flex-1 font-mono`} />
                          <Button onClick={() => setAccessPassword(generatePassword())}>Genera</Button>
                        </div>
                      )}
                    </Field>
                    {!accessPassword && <p className="text-xs text-warn">Genera la password: senza, l'utente non si crea.</p>}
                  </>
                )}
              </>
            )}
          </fieldset>
        )}

        {/*
          * IL CID NON SI DIGITA E DA QUI NON SI MODIFICA: e' un'IDENTITA'.
          *
          * Lo genera il backend alla creazione del cliente, e resta lo stesso
          * — fra fasi, fra conti prop bruciati e rifatti, fra cicli — finche'
          * qualcuno non lo cambia di proposito dalla matita nella scheda del
          * cliente, che e' una rotta sua con conferma e audit.
          * Due CID per lo stesso cliente vogliono dire, per la prop firm, due
          * macchine: la correlazione che il proxy dedicato si paga per rompere,
          * vista dall'altro lato.
          *
          * Un campo di testo qui sarebbe un CID che si puo' scrivere sbagliato,
          * e uno sbagliato non da' un errore: da' una connessione che riesce
          * presentando un dispositivo diverso. Il `PATCH` lo rifiuta comunque.
          */}
        {client && (
          <Field label="CID" hint="Generato alla creazione. Si cambia dalla matita nella scheda del cliente, con conferma: la prop firm vedrà un dispositivo nuovo.">
            {() => <p className="machine-id py-2">{cid || "—"}</p>}
          </Field>
        )}

        <fieldset className="space-y-3 rounded border border-line p-3">
          <legend className="px-1 text-sm text-muted">Quote del cliente</legend>
          <p className="text-xs text-faint">
            Sono PREDEFINITI, non regole: il ciclo se le congela alla nascita, come il CID. Cambiarle qui non tocca i cicli già
            aperti — vale dal prossimo. All'atto pratico si conferma un importo, non una percentuale: questi numeri lo propongono.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sui payout (%)" hint="Del netto che la prop firm ci versa">
              {(id) => (
                <input id={id} inputMode="decimal" value={payoutSplit} onChange={(e) => setPayoutSplit(e.target.value)} className={inputClass} />
              )}
            </Field>
            <Field label="Alla bruciatura (%)" hint="Del netto del ciclo, quando il conto prop brucia">
              {(id) => (
                <input id={id} inputMode="decimal" value={burnSplit} onChange={(e) => setBurnSplit(e.target.value)} className={inputClass} />
              )}
            </Field>
          </div>
          {(!pctToFraction(payoutSplit) || !pctToFraction(burnSplit)) && (
            <p className="text-xs text-loss">Le quote si scrivono come percentuali tra 0 e 100.</p>
          )}
        </fieldset>

        <fieldset className="space-y-3 rounded border border-line p-3">
          <legend className="px-1 text-sm text-muted">Proxy del cliente</legend>
          <p className="text-xs text-faint">
            Uno per cliente: due clienti dallo stesso indirizzo se li fa collegare la prop firm, ed è la cosa che il proxy dedicato
            esiste per evitare. Host e porta vuoti: nessun proxy, e allora i conti prop non si connettono.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Host">
                {(id) => (
                  <input id={id} value={host} placeholder="geo.iproyal.com" onChange={(e) => setHost(e.target.value)} className={inputClass} />
                )}
              </Field>
            </div>
            <Field label="Porta">
              {(id) => (
                <input
                  id={id}
                  inputMode="numeric"
                  value={port}
                  placeholder="11200"
                  onChange={(e) => setPort(e.target.value.replace(/[^0-9]/g, ""))}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              {(id) => (
                <select id={id} value={type} onChange={(e) => setType(e.target.value as "SOCKS5" | "HTTP" | "HTTPS")} className={inputClass}>
                  {(["SOCKS5", "HTTP", "HTTPS"] as const).map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Fornitore" hint="Decide come si compongono i parametri.">
              {(id) => (
                <input id={id} value={provider} placeholder="iproyal" onChange={(e) => setProvider(e.target.value)} className={inputClass} />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username">
              {(id) => <input id={id} value={username} autoComplete="off" onChange={(e) => setUsername(e.target.value)} className={inputClass} />}
            </Field>
            <Field label="Password" hint={proxy ? "Vuoto: resta quella di adesso." : "Solo il segreto: i parametri stanno qui sotto."}>
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                />
              )}
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Paese">
              {(id) => <input id={id} value={country} placeholder="IT" onChange={(e) => setCountry(e.target.value)} className={inputClass} />}
            </Field>
            <Field label="Città">
              {(id) => <input id={id} value={city} placeholder="Milan" onChange={(e) => setCity(e.target.value)} className={inputClass} />}
            </Field>
          </div>
          {proxy?.sessionId && (
            <p className="text-xs text-faint">
              Sessione <span className="machine-id">{proxy.sessionId}</span>: è il suffisso che decide l'indirizzo d'uscita. Deriva dal
              cliente e non si condivide con nessun altro.
            </p>
          )}
          {proxyInvalid && <p className="text-xs text-loss">Per un proxy servono host e una porta valida.</p>}
        </fieldset>

        <Field label="Note">
          {(id) => <textarea id={id} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

// ---------------------------------------------------------------------------
// Conto
// ---------------------------------------------------------------------------

/**
 * Un conto MT5. CID e proxy NON ci sono: sono del cliente (DECISIONE 19). Qui
 * si mostra soltanto con che cosa il conto andra' a mercato, in sola lettura —
 * un secondo posto dove scriverli sarebbe un posto da cui un giorno esce un
 * valore diverso da quello che il gestionale mostra.
 */
export function AccountPanel({
  open,
  clientId,
  clientDefaults,
  account,
  defaultRole = "PROP",
  onClose,
}: {
  open: boolean;
  clientId: string;
  clientDefaults: { computerId: string; defaultProxy: ProxyPublic | null };
  account: Account | null;
  defaultRole?: "PROP" | "BROKER" | "NONE";
  onClose: () => void;
}) {
  const refresh = useRefreshDirectory();
  const servers = useServers();
  const firms = usePropFirms();

  const [label, setLabel] = useState("");
  const [role, setRole] = useState<"PROP" | "BROKER" | "NONE">(defaultRole);
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [serverId, setServerId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [notes, setNotes] = useState("");

  const [newServer, setNewServer] = useState(false);
  const [serverName, setServerName] = useState("");
  const [serverFirmId, setServerFirmId] = useState("");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLabel(account?.label ?? "");
    setRole(account?.role ?? defaultRole);
    setLogin(account?.mt5Login ?? "");
    setPassword("");
    setServerId(account?.serverId ?? "");
    setCurrency(account?.currency ?? (defaultRole === "BROKER" ? "EUR" : "USD"));
    setNotes(account?.notes ?? "");
    setNewServer(false);
    setServerName("");
    setServerFirmId("");
    setError(null);
  }, [open, account, defaultRole]);

  const editing = !!account;
  const locked = !!account?.isConnected; // ruolo e password solo a conto disconnesso
  const kind = role === "NONE" ? null : role;
  const serverOptions = (servers.data ?? []).filter((s) => !kind || s.kind === kind);

  // Il proxy del cliente vale solo sui conti prop.
  const proxyInUso = role === "PROP" ? clientDefaults.defaultProxy : null;
  const propWithoutProxy = role === "PROP" && !clientDefaults.defaultProxy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        // Solo ciò che è cambiato.
        const patch: AccountPatch = {};
        if (label.trim() !== account.label) patch.label = label.trim();
        if (role !== account.role) patch.role = role;
        if (password) patch.mt5Password = password;
        if (currency !== account.currency) patch.currency = currency;
        if (orNull(notes) !== account.notes) patch.notes = orNull(notes);
        if (Object.keys(patch).length) await api.accounts.update(account.id, patch);
      } else {
        let sid = serverId;
        if (newServer) {
          if (!kind) throw new Error("Scegli il ruolo prima di creare il server: prop e broker usano server diversi");
          const created = await api.servers.create({
            name: serverName.trim(),
            kind,
            propFirmId: kind === "PROP" ? orNull(serverFirmId) : null,
          });
          sid = created.id;
        }
        await api.accounts.create({
          label: label.trim(),
          clientId,
          role,
          mt5Login: login.trim(),
          mt5Password: password,
          serverId: sid,
          currency,
          notes: orNull(notes),
        });
      }
      await refresh();
      onClose();
    } catch (err) {
      await refresh(); // un server creato prima dell'errore deve comparire nella tendina
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={editing ? `Modifica ${account.label}` : "Nuovo conto"}
      subtitle={locked ? "Conto connesso: ruolo e password si cambiano solo dopo averlo disconnesso." : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="account-form" disabled={busy}>
            {busy ? "Salvataggio…" : editing ? "Salva" : "Aggiungi conto"}
          </Button>
        </>
      }
    >
      <form id="account-form" onSubmit={submit} className="space-y-4">
        <Field label="Ruolo">
          {(id) => (
            <div id={id} role="radiogroup" className="grid grid-cols-3 gap-2">
              {(["PROP", "BROKER", "NONE"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={role === r}
                  disabled={locked}
                  onClick={() => {
                    setRole(r);
                    if (!editing) setServerId("");
                  }}
                  className={[
                    "rounded border px-2 py-1.5 text-sm transition-colors disabled:opacity-50",
                    role === r
                      ? r === "PROP"
                        ? "border-prop bg-prop/15 text-prop"
                        : r === "BROKER"
                          ? "border-broker bg-broker/15 text-broker"
                          : "border-muted bg-raised text-ink"
                      : "border-line text-muted hover:text-ink",
                  ].join(" ")}
                >
                  {r === "PROP" ? "Prop" : r === "BROKER" ? "Broker" : "Nessun ruolo"}
                </button>
              ))}
            </div>
          )}
        </Field>

        <Field label="Etichetta">
          {(id) => (
            <input id={id} required value={label} placeholder="nastasi next1" onChange={(e) => setLabel(e.target.value)} className={inputClass} />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Login MT5">
            {(id) => (
              <input
                id={id}
                required
                inputMode="numeric"
                disabled={editing}
                value={login}
                onChange={(e) => setLogin(e.target.value.replace(/\D/g, ""))}
                className={`${inputClass} font-mono`}
              />
            )}
          </Field>
          <Field label="Valuta">
            {(id) => (
              <input
                id={id}
                required
                maxLength={3}
                value={currency}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                className={inputClass}
              />
            )}
          </Field>
        </div>

        <Field
          label="Password MT5"
          hint={editing ? "Lascia vuoto per non cambiarla. Non viene mai mostrata: si può solo sostituire." : "Cifrata prima di essere salvata. Non viene mai mostrata."}
        >
          {(id) => (
            <input
              id={id}
              type="password"
              required={!editing}
              disabled={locked}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          )}
        </Field>

        {editing ? (
          <Field label="Server" hint="Login e server identificano il conto anche su Mirror: non si cambiano.">
            {(id) => <input id={id} disabled value={account.server.name} className={inputClass} />}
          </Field>
        ) : !newServer ? (
          <Field
            label="Server MT5"
            hint={
              <button type="button" className="text-muted underline-offset-2 hover:text-ink hover:underline" onClick={() => setNewServer(true)}>
                Il server non è in elenco? Aggiungilo
              </button>
            }
          >
            {(id) => (
              <select id={id} required value={serverId} onChange={(e) => setServerId(e.target.value)} className={inputClass}>
                <option value="" disabled>
                  {serverOptions.length ? "Scegli il server" : `Nessun server ${kind === "PROP" ? "prop" : kind === "BROKER" ? "broker" : ""}: aggiungilo`}
                </option>
                {serverOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.displayName ?? s.name}
                    {s.propFirm ? ` · ${s.propFirm.name}` : ""}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : (
          <fieldset className="space-y-3 rounded border border-line p-3">
            <legend className="px-1 text-sm text-muted">Nuovo server {kind === "PROP" ? "prop" : kind === "BROKER" ? "broker" : ""}</legend>
            <Field label="Nome esatto del server MT5" hint="Come compare nel terminale, per esempio FundedNext-Server 2.">
              {(id) => <input id={id} required value={serverName} onChange={(e) => setServerName(e.target.value)} className={inputClass} />}
            </Field>
            {kind === "PROP" && (
              <Field label="Prop firm">
                {(id) => (
                  <select id={id} value={serverFirmId} onChange={(e) => setServerFirmId(e.target.value)} className={inputClass}>
                    <option value="">Non indicata</option>
                    {(firms.data ?? []).map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            <button type="button" className="text-sm text-muted hover:text-ink" onClick={() => setNewServer(false)}>
              Scegli un server esistente
            </button>
          </fieldset>
        )}

        {/* CID e proxy sono del CLIENTE: qui si legge con che cosa il conto va a
            mercato, e si cambia dalla scheda del cliente. */}
        <div className="rounded border border-line bg-raised/40 p-3 text-sm">
          <p className="text-xs text-faint">Con che cosa va a mercato</p>
          <p className="mt-1">
            CID{" "}
            {clientDefaults.computerId ? (
              <span className="machine-id">{clientDefaults.computerId}</span>
            ) : (
              <span className="text-muted">nessuno: lo genera il gateway a ogni accesso</span>
            )}
          </p>
          <p>
            Proxy{" "}
            {proxyInUso ? (
              <span className="text-ink">
                {proxyName(proxyInUso)}
                {[proxyInUso.city, proxyInUso.country].filter(Boolean).length
                  ? ` · ${[proxyInUso.city, proxyInUso.country].filter(Boolean).join(", ")}`
                  : ""}
              </span>
            ) : role === "BROKER" ? (
              <span className="text-muted">nessuno: la gamba broker non passa dal proxy</span>
            ) : (
              <span className="text-warn">nessuno: senza, il conto prop non si connette</span>
            )}
          </p>
          <p className="mt-2 text-xs text-faint">
            Sono del cliente e si cambiano dalla sua scheda. Con un ciclo aperto il CID è quello congelato all'apertura.
          </p>
          {propWithoutProxy && (
            <p className="mt-1 text-xs text-warn">Assegna un proxy al cliente prima di provare a connettere il conto prop.</p>
          )}
        </div>

        <Field label="Note">
          {(id) => <textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputClass} />}
        </Field>

        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}
